<script setup lang="ts">
import { computed, ref } from 'vue';
import Dialog from 'primevue/dialog';
import RadioButton from 'primevue/radiobutton';
import InputText from 'primevue/inputtext';
import FloatLabel from 'primevue/floatlabel';
import { ButtonType, CustomButton, RollingIntervalSelector } from '@/components';
import { useFormBuilder } from '@/stores/formBuilder';
import { type Reminders, getEmptyReminderConfig } from '@/models';

const builder = useFormBuilder();

defineProps({
  visible: {
    type: Boolean,
    required: true
  }
});

const reminder = builder.form?.reminders ? builder.form?.reminders : getEmptyReminderConfig();

const emits = defineEmits(['onClose']);

const closeDialog = () => {
  emits('onClose');
};

const initialReminderConfig = ref<Reminders>(JSON.parse(JSON.stringify(reminder)));
const reminderConfig = ref<Reminders>(JSON.parse(JSON.stringify(reminder)));

const endsSelection = ref(reminderConfig.value.ends ? '1' : '2');
const endsChange = () => {
  reminderConfig.value.ends = endsSelection.value === '1' ? reminderConfig.value.ends : null;
};

const isPristine = computed(() => JSON.stringify(initialReminderConfig.value) !== JSON.stringify(reminderConfig.value));


const saveReminderConfig = () => {
    builder.form!.reminders = reminderConfig.value;
    closeDialog();
};
</script>

<template>
    <div class="dialog-root">
        <Dialog :visible="visible"
            header="Set reminder"
            modal
            class="dialog"
            append-to="self"
            @update:visible="closeDialog">
            <div class="content-root">
                <div class="reminder-type">
                    <div class="title reminder-type-label">Type</div>
                    <div class="reminder-type-selection">
                        <div class="reminder-type-radio">
                            <RadioButton v-model="reminderConfig.type"
                                inputId="rolling"
                                name="rolling"
                                value="rolling"/>
                            <label for="rolling" class="ml-2">Rolling</label>
                        </div>
                        <div class="reminder-type-radio">
                            <RadioButton v-model="reminderConfig.type"
                                inputId="custom"
                                name="custom"
                                value="fixed"/>
                            <label for="custom" class="ml-2">Custom</label>
                        </div>
                    </div>
                </div>

                <hr class="divider reminder-divider"/>

                <div class="interval">
                    <div class="title interval-label">Interval</div>

                    <div class="interval-config">
                        <RollingIntervalSelector :type="reminderConfig.type"
                            v-model:cronValue="reminderConfig.recurrence"
                            v-model:random="reminderConfig.randomness"
                            v-model:timezone="reminderConfig.timezone"/>

                        <div class="ends-config">
                            <div class="title ends-label">Ends</div>
                            <div class="ends-interval-radio-1">
                                <RadioButton v-model="endsSelection" value="1" @change="endsChange"/>
                                <label for="rolling" class="ml-2">After</label>
                                <InputText id="endLabel"
                                    type="number"
                                    v-model="reminderConfig.ends"
                                    :disabled="endsSelection === '2'"
                                    class="ends-input"/>
                                <label for="rolling" class="ml-2">intervals</label>
                            </div>
                            <div class="ends-interval-radio-2">
                                <RadioButton v-model="endsSelection" value="2" @change="endsChange"/>
                                <label for="custom" class="ml-2">Never</label>
                            </div>
                        </div>
                    </div>
                </div>

                <hr class="divider internal-divider"/>

                <div class="push-notification">
                    <div class="title notification-label">Push notification</div>
                    <FloatLabel class="notification-subject-row">
                        <label for="subject">Subject</label>
                        <InputText id="subject"
                            type="text"
                            v-model="reminderConfig.notification.subject"
                            class="notification-subject-input"/>
                    </FloatLabel>
                    <FloatLabel class="notification-body-row">
                        <label for="body">Body</label>
                        <InputText id="body"
                            type="text"
                            v-model="reminderConfig.notification.body"
                            class="notification-body-input"/>
                    </FloatLabel>
                </div>
            </div>

            <div class="footer">
              <div class="empty"></div>
              <div class="footer-buttons">
                <CustomButton text="Cancel"
                  @onClick="closeDialog"
                  :margin-left="16"
                  :type="ButtonType.OUTLINED"/>
                <CustomButton text="Save"
                  @onClick="saveReminderConfig"
                  :disabled="!isPristine"
                  :margin-left="16"
                  :type="ButtonType.POSITIVE"/>
              </div>
            </div>
        </Dialog>
    </div>
</template>

<style scoped lang="scss">
@import "../../../../assets/dialog";
:deep(.p-dialog-header) {
  padding: unset;
  padding: 40px 40px 0px 40px;
}
:deep(.p-dialog-content) {
  padding: unset;
}
.content-root {
    padding: 0 40px 0 40px;
    width: 1000px;

    .title {
        font-weight: 600;
        font-size: 16px;
        padding-bottom: 8px;
    }

    .divider {
        height: 1px;
        background-color: rgba(0, 0, 0, 0.12);
    }

    .reminder-type {
        padding: 24px 0 24px 0;

        .reminder-type-radio {
            display: flex;
            align-items: center;
            margin-top: 8px;

            .ml-2 {
                margin-left: 8px;
            }
        }
    }

    .interval {
        padding: 24px 0 24px 0;
    }

    .ends-config {
        padding: 24px 0 0 0;

        .ends-interval-radio-1, .ends-interval-radio-2 {
            display: flex;
            align-items: center;

            .ml-2 {
                margin-left: 8px;
                margin-right: 8px;
            }

            .ends-input {
                width: 80px;
            }
        }

        .ends-interval-radio-2 {
            margin-top: 16px;
        }
    }

    .push-notification {
        padding: 24px 0 24px 0;

        .notification-subject-row, .notification-body-row {
            margin-top: 32px;
        }

        margin-bottom: 50px;
    }
}
.footer {
  padding: 12px 40px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #ced3d9;
  background-color: #f5f7f7;
  position: absolute;
  bottom: 0;
  width: 100%;
  z-index: 9999;

  .footer-buttons {
    display: flex;
  }
}
</style>
